import { GetEventOverrides, SendTestRecord } from '@/connection/integrations/integrations';
import { getUser } from '@/store/slices/user/selectors';
import { AvailableInstance, EMappedIntegrationTypes, SolutionImages } from '@/types/integrations.types';
import Modal from '@ui/modal/modal';
import OptionalComponent from '@ui/optional-component/optional-component';
import Switch from '@ui/switch/switch';
import toastTrigger from '@ui/toast/toast-trigger';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggleEventInstance } from '@/store/slices/integrations';
import AdminCard from '@ui/cards/admin/admin-card';

import './integrations-event-details-card.css';
import Container from '@ui/container/container';
import EventIntegrationOverridesModal from './integrations-mapped-modal/event-integration-overrides-modal';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

import './integrations-event-details-card.css';

interface IntegrationsEventDetailsCardProps {
  solutionTitle?: string;
  instance: AvailableInstance;
  projectUuid: string;
}

const IntegrationsEventDetailsCard: React.FC<IntegrationsEventDetailsCardProps> = ({
  solutionTitle,
  instance,
  projectUuid,
}) => {
  const user = useAppSelector(getUser);

  const [testRecordSent, setTestRecordSent] = useState(false);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const enabled = instance.integrations_event?.event_uuid === projectUuid && instance.enabled;
  const canOverride = [EMappedIntegrationTypes.registration, EMappedIntegrationTypes.activity].includes(instance.type);
  const canSendTestRecord = [EMappedIntegrationTypes.registration].includes(instance.type);
  const image = SolutionImages.get(solutionTitle ?? '');

  const dispatch = useAppDispatch();

  const handleTestRecord = useCallback(
    async (instance: AvailableInstance, entity_uuid?: string) => {
      if (!testRecordSent && entity_uuid && user) {
        toastTrigger(
          <div>
            <h5 className="H5">Sending Test Record</h5>
            <p className="S1">Sending a test record to {instance.name}.</p>
          </div>,
          {
            type: 'info',
            autoClose: 5000,
          }
        );
        setTestRecordSent(true);
        try {
          setResponseModalOpen(true);
          const { entity_mapping, channel_mapping } = await GetEventOverrides(
            instance.instance_uuid,
            entity_uuid,
            user.active_channel
          );
          if (entity_mapping !== undefined || channel_mapping !== undefined) {
            const { response } = await SendTestRecord(user.active_channel, channel_mapping, {
              ...entity_mapping,
              action: {
                action_endpoint: channel_mapping.action_endpoint,
                action_label: channel_mapping.action_label,
                action_description: channel_mapping.action_description,
              },
            });
            setResponseMessage(response);
          }
        } catch (e: unknown) {
          console.error(e);
          toastTrigger(
            <div>
              <h5 className="H5">Error</h5>
              <p className="S1">Error sending test record.</p>
            </div>,
            {
              type: 'error',
              autoClose: 5000,
            }
          );
          setResponseModalOpen(false);
          setResponseMessage('');
        } finally {
          setTestRecordSent(false);
        }
      }
    },
    [testRecordSent, user]
  );

  const handleClose = () => {
    setResponseModalOpen(false);
    setResponseMessage('');
  };

  const toggleInstance = () => {
    if (projectUuid && user) {
      dispatch(
        toggleEventInstance({
          instance_uuid: instance.instance_uuid,
          event_uuid: projectUuid,
          channel: user.active_channel,
          enable: !enabled,
        })
      );
    }
  };

  const getFormattedResponseMessage = () => {
    try {
      const parsedJson = JSON.parse(responseMessage);
      return JSON.stringify(parsedJson, null, 2);
    } catch {
      return responseMessage;
    }
  };

  return (
    <AdminCard
      className={classNames(`integrations-event-details-card`, { 'integration-off': !enabled })}
      key={instance.instance_uuid}
    >
      <div className="integration-header">
        <div className="integration-logo">
          <OptionalComponent display={!!image}>
            <img src={image} alt={solutionTitle} />
          </OptionalComponent>
        </div>
        <Switch value={instance?.instance_uuid} disabled={!instance.enabled} on={enabled} onClick={toggleInstance} />
      </div>
      <div className="integration-text">
        <span className="text-ellipsis">{solutionTitle}</span>
        <p className="text-ellipsis S1">{instance.name}</p>
        <span className="integration-type-label text-ellipsis">{`${instance.type.toString()} integration`}</span>
      </div>
      <div className="integration-footer">
        <OptionalComponent display={canOverride}>
          <Button
            backgroundColor={getThemeColorCSSVariable()}
            padding="8px 16px"
            width="auto"
            onClick={() => setConfigModalOpen(true)}
            hoverOpacity={0.8}
          >
            <Text
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              className="L2"
            >
              Edit
            </Text>
          </Button>
        </OptionalComponent>
        <OptionalComponent display={canSendTestRecord}>
          <Button
            className="padding-v-8 padding-h-16 send-test"
            onClick={() => handleTestRecord(instance, projectUuid)}
            disabled={!enabled}
            backgroundColor={getThemeColorCSSVariable()}
          >
            <Text
              className="L2"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              {testRecordSent ? 'Submitted' : 'Send test record'}
            </Text>
          </Button>
        </OptionalComponent>
      </div>
      {/* Use optional component to avoid rendering the modal if it's not open */}
      <OptionalComponent display={responseModalOpen}>
        <Modal
          size="large"
          contentWrapperClassName="padding-v-24 padding-h-40"
          RenderHeaderContent={() => <h2 className="H2 margin-b-20">Response</h2>}
          open={responseModalOpen}
          onRequestClose={handleClose}
        >
          <Container
            background="var(--gray-900)"
            borderWidth="2px"
            borderColor="var(--gray-700)"
            className="word-break radius-12 padding-8"
            loading={responseMessage.length === 0}
          >
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{getFormattedResponseMessage()}</pre>
          </Container>
        </Modal>
      </OptionalComponent>
      <OptionalComponent display={configModalOpen}>
        <EventIntegrationOverridesModal
          instance={instance}
          onClose={() => setConfigModalOpen(false)}
          solutionTitle={solutionTitle}
        />
      </OptionalComponent>
    </AdminCard>
  );
};

export default IntegrationsEventDetailsCard;
